import { useState } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { PermissionGate } from "@/components/PermissionGate";
import { LogOut, Building2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { ThemeToggle } from "@/components/ThemeToggle";
import { Sidebar } from "@/components/layout/Sidebar";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

function CompanySelector() {
  const { companies, selectedCompanyId, setSelectedCompanyId, loading } = useCompany() || {};
  if (loading || !companies || companies.length === 0) return null;
  return (
    <Select value={selectedCompanyId} onValueChange={setSelectedCompanyId}>
      <SelectTrigger
        data-testid="company-selector"
        className="h-9 w-44 gap-1.5 border-border bg-background text-xs sm:w-52"
      >
        <Building2 className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        <SelectValue placeholder="Select company" />
      </SelectTrigger>
      <SelectContent>
        {companies.map((c) => (
          <SelectItem key={c.id} value={c.id} data-testid={`company-option-${c.code}`}>
            {c.name}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem("dpr_sidebar_collapsed") === "true");

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      localStorage.setItem("dpr_sidebar_collapsed", String(!c));
      return !c;
    });
  };

  return (
    <div className="flex min-h-screen bg-background">
      <Sidebar collapsed={collapsed} onToggleCollapse={toggleCollapsed} />
      <div className="flex min-h-screen flex-1 flex-col">
        <header className="sticky top-0 z-40 flex h-16 shrink-0 items-center justify-end gap-3 border-b border-border bg-card px-4 sm:px-6">
          <CompanySelector />
          <ThemeToggle className="hover:border-gold-500 hover:text-gold-500" />
          <div className="hidden text-right sm:block">
            <p className="text-sm font-medium leading-tight text-foreground" data-testid="current-user-name">{user?.full_name}</p>
            <p className="text-[11px] font-semibold leading-tight text-gold-600 dark:text-gold-400" data-testid="current-user-role">{user?.role}</p>
          </div>
          <button
            data-testid="logout-button"
            onClick={logout}
            className="flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground hover:border-red-500/40 hover:bg-red-500/10 hover:text-red-500"
            title="Log out"
          >
            <LogOut className="h-4 w-4" />
          </button>
        </header>
        <main className="flex-1 space-y-5 p-4 sm:p-6">
          <PermissionGate><Outlet /></PermissionGate>
        </main>
      </div>
    </div>
  );
}

